import { TicketStatus, type TicketListItem, type TicketListQuery } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PriorityBadge, StatusBadge } from '@/products/helpdesk/features/tickets/ticket-badges';
import { useTickets } from '@/products/helpdesk/features/tickets/use-tickets';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { PersonChip } from '@/platform/components/person-chip';

export function HomeTicketQueue({
  title,
  href,
  query,
  emptyLabel,
}: {
  title: string;
  href: string;
  query: TicketListQuery;
  emptyLabel: string;
}) {
  const tickets = useTickets(query);

  return (
    <Card className="min-w-0 gap-0 overflow-hidden py-0 shadow-sm">
      <CardHeader className="border-b py-4">
        <CardTitle className="min-w-0 text-base">
          <h2 className="truncate text-base font-semibold">{title}</h2>
        </CardTitle>
        {tickets.data ? (
          <p className="min-w-0 truncate text-sm text-muted-foreground">
            {tickets.data.total === 0
              ? 'Keine Einträge'
              : `${tickets.data.total} Ticket${tickets.data.total === 1 ? '' : 's'}`}
          </p>
        ) : null}
        <CardAction>
          <Button asChild variant="outline" size="sm">
            <Link to={href}>Alle anzeigen</Link>
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="min-w-0 px-0 py-0">
        {tickets.isPending && (
          <p className="flex items-center gap-2 px-6 py-4 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Tickets werden geladen …
          </p>
        )}
        {tickets.isError && (
          <p role="alert" className="px-6 py-4 text-sm text-destructive">
            {tickets.error instanceof ApiRequestError
              ? tickets.error.message
              : 'Die Liste konnte nicht geladen werden.'}
          </p>
        )}
        {tickets.data?.items.length === 0 && (
          <p className="px-6 py-4 text-sm text-muted-foreground">{emptyLabel}</p>
        )}
        {tickets.data && tickets.data.items.length > 0 && (
          <ul className="divide-y">
            {tickets.data.items.map((item) => (
              <HomeTicketRow key={item.id} item={item} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function HomeTicketRow({ item }: { item: TicketListItem }) {
  return (
    <li>
      <Link
        to={`/helpdesk/tickets/${item.number}`}
        className="flex min-w-0 flex-col gap-1 px-4 py-3 hover:bg-muted/50 sm:flex-row sm:items-center sm:justify-between sm:px-6"
      >
        <div className="min-w-0">
          <p className="truncate font-medium">
            #{item.number} · {item.subject}
          </p>
          <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
            <span>Von</span>
            <PersonChip
              displayName={item.requester.displayName}
              profilePhotoUrl={item.requester.profilePhotoUrl}
              avatarClassName="size-5"
            />
            <span className="text-muted-foreground/80">
              {' '}
              · Aktualisiert {formatDateTime(item.updatedAt)}
            </span>
          </p>
        </div>
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          <StatusBadge status={item.status} />
          <PriorityBadge priority={item.priority} />
        </span>
      </Link>
    </li>
  );
}

export const OPEN_TICKET_STATUSES = [
  TicketStatus.NEW,
  TicketStatus.IN_PROGRESS,
  TicketStatus.WAITING_FOR_CUSTOMER,
  TicketStatus.WAITING_FOR_APPROVAL,
  TicketStatus.REOPENED,
] as const;
